import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Megaphone, TrendingUp, Users, MousePointerClick, ShieldCheck, Check, ArrowRight,
  Monitor, LayoutList, Square, BadgeCheck,
} from "lucide-react";

const HERO_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/3cd3e1d7f15ac19df88b6d98adb29788274171132b3ae5e3ee0032c394e925c5.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/9ab3d11fa86c98de03e4686953d81fe15f1482bc5c44efe443dc9087898f662f.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/9aeadad83cae29bed6fb008c75ebc6210c06810b04708b95853e7f501f7928eb.jpeg",
];

const BENEFITS = [
  { icon: Users, title: "Reach engaged pet lovers", body: "Thousands of Canadians browse CritterPosts every week looking for pets, supplies and services." },
  { icon: MousePointerClick, title: "Only pay per campaign", body: "Simple flat pricing from CAD $5/day. No auctions, no hidden fees — pay by Interac e-Transfer." },
  { icon: TrendingUp, title: "Track real results", body: "See live impressions, clicks and CTR for every ad, and download reports anytime." },
  { icon: ShieldCheck, title: "Trusted placements", body: "Your brand appears alongside verified sellers on a clean, modern marketplace." },
];

const PLACEMENTS = [
  { key: "home_top", icon: Monitor, name: "Home top (premium)", desc: "Premium leaderboard at the very top of the homepage, above the listings — the first thing visitors see.", size: "1200 × 120 px" },
  { key: "home", icon: Monitor, name: "Home leaderboard", desc: "Wide banner on the homepage, just below the featured listings — seen by everyone browsing the front page.", size: "1200 × 240 px" },
  { key: "browse", icon: LayoutList, name: "Browse strip", desc: "Slim banner across search results while buyers shop.", size: "1200 × 180 px" },
  { key: "browse_sidebar", icon: Square, name: "Browse sidebar box", desc: "Tall rectangle in the Browse filters sidebar — always in view beside results.", size: "600 × 500 px" },
  { key: "listing", icon: Square, name: "Listing rectangle", desc: "Eye-level rectangle on individual listing pages beside the details.", size: "600 × 500 px" },
];

export default function AdvertisingInfo() {
  const { user } = useAuth();
  const [idx, setIdx] = useState(0);
  const eligible = user && (user.is_verified_seller || user.verified_seller || user.verified_business);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-[#FFF4F0] rounded-full px-3 py-1"><Megaphone size={13} /> Advertise on CritterPosts.ca</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#12234A] mt-4 leading-[1.05]">Put your brand in front of Canada's pet lovers</h1>
          <p className="text-base text-slate-500 mt-4 max-w-lg">Promote your pet business with beautiful banner ads across CritterPosts.ca. Flat, affordable pricing, real performance tracking, and it goes live the moment we confirm your e-Transfer.</p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link to="/advertise"><Button data-testid="advertising-cta-primary" className="rounded-xl h-12 px-6 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">Book your banner <ArrowRight size={17} /></Button></Link>
            <a href="#placements"><Button variant="outline" className="rounded-xl h-12 px-6 font-semibold">See placements</Button></a>
          </div>
          <p className="text-xs text-slate-400 mt-4">From <strong className="text-[#12234A]">CAD $5/day</strong> · 10% off 14+ days · 20% off 30+ days</p>
        </div>
        <div className="relative" data-testid="advertising-hero-carousel">
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl aspect-[3/2] bg-slate-100">
            {HERO_IMAGES.map((src, i) => (
              <img key={i} src={src} alt="Advertising on CritterPosts" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`} />
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            {HERO_IMAGES.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`advertising-hero-dot-${i}`} aria-label={`Show slide ${i + 1}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#F5551E]" : "w-2 bg-slate-300 hover:bg-slate-400"}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-display font-bold text-2xl text-[#12234A] text-center">Why advertise with us</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`advertising-benefit-${b.title.split(" ")[0].toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><b.icon size={20} className="text-[#F5551E]" /></div>
                <h3 className="font-display font-bold text-[#12234A] mt-3">{b.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Placements */}
      <section id="placements" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display font-bold text-2xl text-[#12234A] text-center">Choose your placement</h2>
        <p className="text-sm text-slate-500 text-center mt-2 max-w-xl mx-auto">Four high-visibility slots across the marketplace. Pick one — or run several for full coverage.</p>
        <div className="text-center mt-3">
          <Link to="/advertising/placements" data-testid="advertising-placements-guide-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] hover:gap-2.5 transition-all">
            See exactly where each ad appears <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {PLACEMENTS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-slate-200/80 p-6 hover:border-[#F5551E]/40 hover:shadow-md transition-all" data-testid={`advertising-placement-${p.name.split(" ")[0].toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-[#12234A] flex items-center justify-center"><p.icon size={22} className="text-[#F5551E]" /></div>
              <h3 className="font-display font-bold text-lg text-[#12234A] mt-4">
                <Link to={`/advertising/placements#placement-${p.key}`} className="hover:text-[#F5551E] transition-colors" data-testid={`advertising-placement-link-${p.key}`}>{p.name}</Link>
              </h3>
              <p className="text-sm text-slate-500 mt-1.5">{p.desc}</p>
              <p className="text-xs font-semibold text-slate-400 mt-3">Ideal image: <span className="text-[#F5551E]">{p.size}</span></p>
              <Link to="/advertise"><Button variant="ghost" className="mt-4 px-0 text-[#F5551E] font-semibold gap-1 hover:bg-transparent hover:gap-2 transition-all">Book this slot <ArrowRight size={15} /></Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-[#12234A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 text-center">
          <h2 className="font-display font-bold text-2xl">Simple, honest pricing</h2>
          <p className="text-slate-300 mt-2">Flat daily rate with automatic discounts for longer runs.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8 max-w-3xl mx-auto">
            {[{ d: "1–13 days", p: "$5/day", note: "Standard rate" }, { d: "14–29 days", p: "$4.50/day", note: "10% off" }, { d: "30+ days", p: "$4/day", note: "20% off" }].map((t) => (
              <div key={t.d} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <p className="text-sm text-slate-300">{t.d}</p>
                <p className="font-display font-extrabold text-3xl mt-2">{t.p}</p>
                <p className="inline-flex items-center gap-1 text-xs text-[#F5551E] font-semibold mt-2"><Check size={13} /> {t.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-9">
            {eligible ? (
              <Link to="/advertise"><Button data-testid="advertising-cta-book" className="rounded-xl h-12 px-8 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">Go to booking form <ArrowRight size={17} /></Button></Link>
            ) : (
              <div className="inline-flex flex-col items-center gap-2">
                <span className="inline-flex items-center gap-2 text-sm text-slate-300"><BadgeCheck size={16} className="text-[#F5551E]" /> Banner ads are available to verified business accounts</span>
                <Link to={user ? "/dashboard" : "/advertise"}><Button data-testid="advertising-cta-book" className="rounded-xl h-12 px-8 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{user ? "Become a verified business" : "Get started"} <ArrowRight size={17} /></Button></Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
